// =============================================================================
// RubricPicker.tsx
// =============================================================================
// Vurderingsmatrise-velger med inline opprettelse + info-boks.
// Brukes i ExamCreator (og framtidig LmsMappingModal) når studieplanens
// vurderingsramme krever rubric.
//
// Oppfører seg som en kontrollert komponent — forelder eier `value` og
// `onChange`. Null = ingen matrise valgt (has_rubric utledes til false).
// =============================================================================

import { useEffect, useState } from 'react';
import { ClipboardCheck, Loader2, AlertCircle, Lock, Plus, Info, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { authenticatedFetch, getUser } from '../lib/auth';
import { NewRubricModal } from './RubricManagement';

// Norske labels for enum-verdier — speilet fra RubricManagement + CompositePlanEditor
const COMPLEXITY_LABELS: Record<string, string> = {
  simple: 'enkel',
  graded: 'gradert',
  advanced: 'avansert',
};
const COMPONENT_TYPE_LABELS: Record<string, string> = {
  written: 'skriftlig prøve',
  project: 'prosjektinnlevering',
  oral: 'muntlig',
  practical: 'praktisk demonstrasjon',
  portfolio: 'mappe',
  observation: 'observasjon',
  reflection: 'refleksjonsnotat',
  group: 'gruppearbeid',
};

interface Rubric {
  id: string;
  title: string;
  description: string | null;
  version_number: number;
  is_locked: boolean;
  complexity_mode: 'simple' | 'graded' | 'advanced';
  applicable_component_types: string[];
  member_id: string | null;
  member_name: string | null;
}

interface Props {
  /** Valgt matrise-id. null = ingen valgt */
  value: string | null;
  /** Kalles når bruker endrer valg */
  onChange: (rubricId: string | null) => void;
  /** Når true: ramme krever matrise (vises prominent med rød border hvis ikke valgt) */
  required?: boolean;
  /** Hvis satt, filtrer matriser etter om applicable_component_types inneholder denne */
  filterByComponentType?: 'written' | 'practical' | 'project' | 'oral' | 'observation';
  /** Deaktiver (f.eks. ved submit) */
  disabled?: boolean;
}

export default function RubricPicker({
  value,
  onChange,
  required = false,
  filterByComponentType,
  disabled = false,
}: Props) {
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [infoOpen, setInfoOpen] = useState<boolean>(() => {
    try { return localStorage.getItem('rubric_picker_info_open') !== 'false'; }
    catch { return true; }
  });

  const loadRubrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch('/api/admin/assessment-rubrics');
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Kunne ikke hente vurderingsmatriser');
        setRubrics([]);
      } else {
        const list: Rubric[] = Array.isArray(data.rubrics) ? data.rubrics : [];
        setRubrics(list);
      }
    } catch (err: any) {
      setError(err.message);
      setRubrics([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRubrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleInfo = () => {
    const next = !infoOpen;
    setInfoOpen(next);
    try { localStorage.setItem('rubric_picker_info_open', next ? 'true' : 'false'); } catch { /* ignore */ }
  };

  const handleCreated = async (newRubricId?: string) => {
    setShowCreateModal(false);
    await loadRubrics();
    if (newRubricId) onChange(newRubricId);
  };

  // Vi viser ALLE matriser i dropdown, men grupperer dem i to seksjoner:
  //   1) «Passer for X» (anbefalte — applicable_component_types inkluderer prøvesettets type)
  //   2) «Andre matriser» (ikke merket for prøvesettets type, men fortsatt valgbare).
  // Dette gjør at matriser som ble opprettet for annen komponent-type (vanlig scenario
  // når bruker opprettet manuelt i /settings/rubrics) fortsatt er tilgjengelige — med
  // tydelig varsel om mismatch i selected-info-blokken under.
  const matchesFilter = (r: Rubric) =>
    !filterByComponentType || r.applicable_component_types.includes(filterByComponentType);
  const matchingRubrics = rubrics.filter(matchesFilter);
  const otherRubrics = filterByComponentType ? rubrics.filter(r => !matchesFilter(r)) : [];

  const selectedRubric = value ? rubrics.find(r => r.id === value) : null;
  const selectedMismatch = Boolean(
    selectedRubric && filterByComponentType && !matchesFilter(selectedRubric)
  );
  const missingRequired = required && !value;
  const componentLabel = filterByComponentType ? COMPONENT_TYPE_LABELS[filterByComponentType] : null;
  const user = getUser();
  const manageRoute = user?.role === 'systemadmin' ? '/settings/rubrics' : '/member-settings/rubrics';

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 p-3 border border-gray-200 rounded-md">
        <Loader2 size={14} className="animate-spin" />
        Henter vurderingsmatriser…
      </div>
    );
  }

  return (
    <>
      <div className={`space-y-2 ${required ? 'border-l-4 pl-3 ' + (missingRequired ? 'border-red-400' : 'border-green-400') : ''}`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <label className="block text-sm font-medium text-gray-700">
            <ClipboardCheck size={14} className="inline mr-1" />
            Vurderingsmatrise {required && <span className="text-red-600">*</span>}
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              disabled={disabled}
              className="inline-flex items-center gap-1 text-xs font-medium text-kf-blue-700 hover:text-kf-blue-900 hover:bg-kf-blue-50 px-2 py-1 rounded"
              title="Opprett ny vurderingsmatrise uten å forlate prøvesettet"
            >
              <Plus size={14} />
              Opprett ny matrise
            </button>
            <a
              href={manageRoute}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
              title="Åpner matrise-forvaltning i ny fane"
            >
              <ExternalLink size={12} />
              Forvalt alle
            </a>
          </div>
        </div>

        {/* Info-boks — forklarer hva en vurderingsmatrise er */}
        <div className="rounded-md border border-kf-blue-200 bg-kf-blue-50 overflow-hidden">
          <button
            type="button"
            onClick={toggleInfo}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-kf-blue-900 hover:bg-kf-blue-100"
          >
            <span className="flex items-center gap-2 font-medium">
              <Info size={14} />
              Hva er en vurderingsmatrise?
            </span>
            {infoOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {infoOpen && (
            <div className="px-3 pb-3 text-xs text-kf-blue-900 space-y-2 border-t border-kf-blue-200">
              <p>
                En <b>vurderingsmatrise</b> (rubric) er en strukturert sjekkliste med
                kriterier og nivåer som sensor bruker for å gi en gjennomsiktig,
                etterprøvbar vurdering. Hvert kriterium har flere nivåer (f.eks.
                «Utmerket», «Godt», «Tilstrekkelig», «Ikke bestått») med poeng, og
                attesten refererer til den publiserte matrisen slik at arbeidsgiver
                eller NAV kan se <i>hva</i> som faktisk ble vurdert.
              </p>
              <p>
                <b>Når trengs det?</b> For skriftlig SEB-prøve er matrise valgfritt
                (karakter beregnes fra svar-poeng). For muntlig, praktisk, prosjekt
                og mappe er den sterkt anbefalt — og studieplanens vurderingsramme
                kan sette matrise som påkrevd.
              </p>
              <p>
                <b>Gjenbruk:</b> Når du oppretter en matrise lagres den på
                medlemsorganisasjonen og kan brukes på tvers av prøvesett. Når
                første sensur fullføres låses matrisen (K13-versjonering) — endre
                videre ved å klone en ny versjon.
              </p>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-700 flex items-start gap-2">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
          className={`w-full border rounded-md p-2 text-sm bg-white ${
            missingRequired ? 'border-red-300' : 'border-gray-300'
          } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          <option value="">— Ingen matrise valgt —</option>
          {filterByComponentType ? (
            <>
              {matchingRubrics.length > 0 && (
                <optgroup label={`Passer for ${componentLabel}`}>
                  {matchingRubrics.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.title}
                      {r.is_locked ? ' 🔒' : ''}
                      {` (v${r.version_number})`}
                      {r.member_name && ` — ${r.member_name}`}
                    </option>
                  ))}
                </optgroup>
              )}
              {otherRubrics.length > 0 && (
                <optgroup label="Andre matriser (merket for annen komponent-type)">
                  {otherRubrics.map((r) => (
                    <option key={r.id} value={r.id}>
                      ⚠ {r.title}
                      {r.is_locked ? ' 🔒' : ''}
                      {` (v${r.version_number})`}
                      {` — for ${r.applicable_component_types.map(t => COMPONENT_TYPE_LABELS[t] || t).join(', ')}`}
                    </option>
                  ))}
                </optgroup>
              )}
            </>
          ) : (
            rubrics.map((r) => (
              <option key={r.id} value={r.id}>
                {r.title}
                {r.is_locked ? ' 🔒' : ''}
                {` (v${r.version_number})`}
                {r.member_name && ` — ${r.member_name}`}
              </option>
            ))
          )}
        </select>

        {/* Tom-tilstand-hjelp — vises når ingen matrise finnes i det hele tatt */}
        {rubrics.length === 0 && !error && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 space-y-1">
            <div className="font-medium flex items-center gap-1">
              <AlertCircle size={14} />
              Ingen matriser finnes ennå for din organisasjon
            </div>
            <p>
              Klikk <b>«Opprett ny matrise»</b> over for å lage en nå —
              du blir værende i prøvesett-redigeringen og den nye matrisen
              velges automatisk når du er ferdig.
            </p>
          </div>
        )}

        {/* Hint når matriser finnes, men ingen passer for prøvesettets komponent-type */}
        {rubrics.length > 0 && matchingRubrics.length === 0 && filterByComponentType && !error && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 space-y-1">
            <div className="font-medium flex items-center gap-1">
              <AlertCircle size={14} />
              Ingen matriser er merket for {componentLabel}
            </div>
            <p>
              {otherRubrics.length} matrise{otherRubrics.length === 1 ? '' : 'r'} finnes for andre komponent-typer og vises nederst i listen —
              du kan velge en av dem og legge til <b>{componentLabel}</b> i «Forvalt alle»,
              eller klikke <b>«Opprett ny matrise»</b> over for å lage en som passer direkte.
            </p>
          </div>
        )}

        {selectedRubric && (
          <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-2 space-y-1">
            <div className="flex items-center gap-2">
              {selectedRubric.is_locked && <Lock size={12} className="text-amber-600" />}
              <span className="font-medium">{selectedRubric.title}</span>
              <span className="text-gray-500">v{selectedRubric.version_number}</span>
            </div>
            {selectedRubric.description && (
              <div className="text-gray-500 line-clamp-2">{selectedRubric.description}</div>
            )}
            <div className="text-gray-500">
              Nivå: {COMPLEXITY_LABELS[selectedRubric.complexity_mode] || selectedRubric.complexity_mode}
              {' · '}
              Brukes for: {selectedRubric.applicable_component_types
                .map(t => COMPONENT_TYPE_LABELS[t] || t)
                .join(', ')}
            </div>
            {selectedMismatch && (
              <div className="mt-1 pt-1 border-t border-amber-200 bg-amber-50 -mx-2 -mb-2 px-2 py-1.5 text-amber-900 rounded-b-md flex items-start gap-1">
                <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
                <span>
                  Denne matrisen er merket for{' '}
                  <b>{selectedRubric.applicable_component_types.map(t => COMPONENT_TYPE_LABELS[t] || t).join(', ')}</b>,
                  men prøvesettet er planlagt som{' '}
                  <b>{componentLabel || 'annen vurderingsform'}</b>.
                  Åpne matrisen i «Forvalt alle» og legg til denne komponent-typen hvis den også skal brukes her.
                </span>
              </div>
            )}
          </div>
        )}

        {missingRequired && (
          <div className="text-xs text-red-700 flex items-start gap-1">
            <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
            Studieplanens vurderingsramme krever at prøvesettet er koblet til en matrise. Velg en fra listen eller klikk «Opprett ny matrise» over.
          </div>
        )}

        {!required && !value && rubrics.length > 0 && (
          <div className="text-xs text-gray-500">
            Valgfri — kobles kun hvis studieplanens vurderingsramme krever strukturert matrise.
          </div>
        )}
      </div>

      {showCreateModal && (
        <NewRubricModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
          initialComponentTypes={filterByComponentType ? [filterByComponentType] : undefined}
        />
      )}
    </>
  );
}
